/**
 * F 487 — the owner of the seal/unseal dialog.
 *
 * WHY THIS FILE EXISTS — MEASURED, NOT INFERRED
 * ----------------------------------------------
 * F 429 built the whole feature: the engine (`core/credentials/blockEncryption.ts`),
 * the modal (`BlockSealModal.tsx`) and the "Encrypted Block" item in the
 * drag-handle "Turn into" menu. F 429's own suite is 25 engine-level cases
 * and it passed. The FEATURE STILL DID NOTHING when a human clicked it.
 *
 * The cause, measured in the production browser on 2026-10-25 (build 8e305ac):
 *
 *   1. The operator opens the drag-handle menu, hovers "Turn into", and
 *      clicks "Encrypted Block".
 *   2. The item's `onClick` runs `openEncryptModal()`, which sets the local
 *      state `encModalOpen = true`.
 *   3. Mantine's `Menu.Item` CLOSES THE MENU ON CLICK.
 *   4. The menu closing clears BlockNote's side-menu extension state, so
 *      `block` becomes `undefined`.
 *   5. BlockNote's `SideMenuController` renders the drag-handle menu like this:
 *
 *          return (
 *            <BlockPopover blockId={show ? block?.id : undefined} ...>
 *              {block?.id && <Component />}
 *            </BlockPopover>
 *          );
 *
 *      `block?.id && <Component />` — so the component that OWNS the state is
 *      UNMOUNTED at step 4, ~30ms after the click that was supposed to open
 *      the modal.
 *   6. The `encModalOpen` state is destroyed with it. The modal never mounts.
 *
 * The browser timeline, sampled 30ms/60ms/120ms/250ms/500ms/1000ms after the
 * click: `encInDom` went true→false at t+30ms, `sideMenu` went true→false at
 * t+250ms, and `[data-testid="fc-block-encrypt-modal"]` was absent at EVERY
 * sample. No exception was thrown. The operator's report — "Nothing happened"
 * — is exactly right.
 *
 * The F 429 comment in `RichDragHandleMenu.tsx` says:
 *
 *     "The menu mounts only while the 6-dot is open, so a single set of state
 *      variables at this scope is enough."
 *
 * That is the bug, stated as an assumption. The menu mounts only while the
 * 6-dot is open — AND THE MENU CLOSES THE MOMENT YOU CLICK AN ITEM. The
 * state cannot live in a subtree that the click itself destroys.
 *
 * THE FIX, IN ONE SENTENCE
 * -------------------------
 * The state lives in a component that is NOT inside BlockNote's side-menu
 * tree, so the click that closes the menu cannot destroy it.
 *
 * `PageEditor` renders `<BlockSealController>` OUTSIDE `<BlockNoteView>`, and
 * the menu item calls `openSealDialog(pageId, blockId)` through a context.
 * The same context serves the slash menu, so F 487 Stage 3's "Encrypted
 * Block" in the `/` menu is the SAME code path as the drag-handle one rather
 * than a second implementation that can drift.
 */

import * as React from 'react';
import {
  encryptBlock,
  decryptBlock,
  isBlockEncrypted,
  blockAnchors,
  setBlockAnchors,
} from '../../../core/credentials/blockEncryption';
import { BlockSealModal } from './BlockSealModal';
import { setSealOpener } from '../database/encryptedBlockSuggestion';

export interface BlockSealContextValue {
  /**
   * Open the seal dialog for one block.
   *
   * Returns false when there is nothing to seal — no page id, or no block
   * id — so a caller can render the item as unavailable instead of
   * pretending to work.
   */
  openSealDialog: (pageId: string | null | undefined, blockId: string) => boolean;
  /**
   * Called after a successful seal or unseal, with the block that changed.
   * The owner uses it to re-read the page's blocks and refresh the editor.
   */
  onSealedBlockChange?: (blockId: string, action: 'encrypt' | 'decrypt') => void;
}

export const BlockSealContext = React.createContext<BlockSealContextValue | undefined>(undefined);

/** The shape of a public tag as the dialog edits it. */
export interface BlockSealTag {
  id: string;
  text: string;
  kind?: string;
  source?: string;
}

/** The seal dialog API, or `undefined` when there is no provider. */
export function useBlockSeal(): BlockSealContextValue | undefined {
  return React.useContext(BlockSealContext);
}

export interface BlockSealControllerProps {
  /**
   * The tree that can OPEN the seal dialog — the editor, and through it the
   * drag-handle menu and the slash menu.
   *
   * WHY CHILDREN, RATHER THAN A SEPARATE PROVIDER
   * ----------------------------------------------
   * The dialog's state must live OUTSIDE BlockNote's side-menu tree (that
   * subtree is unmounted by the click that opens the dialog), but the menu
   * that OPENS it lives INSIDE that tree. A context only reaches a consumer
   * that is a DESCENDANT of its provider.
   *
   * So the provider has to wrap BOTH: the editor (so the menu can call
   * `openSealDialog`) and the dialog (so the dialog's `opened` state is the
   * controller's). That means one component that is a provider AND the
   * dialog's owner, wrapping the editor in `PageEditor`:
   *
   *     <BlockSealController pageId={...}>
   *       ...the editor...
   *     </BlockSealController>
   *
   * The controller itself is NOT inside `<BlockNoteView>`, so the click
   * that closes the menu cannot unmount it.
   */
  children?: React.ReactNode;
  /**
   * The page currently being edited.
   *
   * The slash-menu item (F 487 Stage 3) needs the page to seal INTO, and it
   * fires while no dialog is open — so the page id cannot come from the
   * dialog's own state. It comes from the editor, which is this
   * component's caller.
   */
  pageId?: string | null;
  /**
   * Called after a successful seal/unseal. The editor refresh is the
   * caller's business — this component does not know about BlockNote.
   */
  onSealedBlockChange?: (blockId: string, action: 'encrypt' | 'decrypt') => void;
}

/**
 * F 487 — owns the seal dialog's state and renders the modal.
 *
 * MOUNT THIS OUTSIDE `<BlockNoteView>`. That is the whole point of this
 * component: the drag-handle menu lives inside BlockNote's tree and is
 * unmounted the moment a menu item is clicked, so a modal whose `opened`
 * state lives there can never be seen.
 */
export const BlockSealController: React.FC<BlockSealControllerProps> = ({
  children,
  pageId: currentPageId,
  onSealedBlockChange,
}) => {
  const [pageId, setPageId] = React.useState<string | null>(null);
  const [blockId, setBlockId] = React.useState<string | null>(null);
  const [opened, setOpened] = React.useState(false);
  const [passphrase, setPassphrase] = React.useState('');
  const [anchor, setAnchor] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  /** True when the target block is ALREADY sealed, so the dialog opens it. */
  const [opening, setOpening] = React.useState(false);
  /**
   * F 487 — the tags on the target block.
   *
   * The operator's flow is: seal a block, THEN see the tag the content
   * produced and adjust it. So the tags are loaded when the dialog opens
   * (from the STORED page content, which is the authority — see
   * `mergeSealedBlocks.ts`) and can be edited without the passphrase,
   * because a tag is plaintext by design.
   */
  const [tags, setTags] = React.useState<BlockSealTag[]>([]);

  const close = React.useCallback(() => {
    setOpened(false);
    setPassphrase('');
    setAnchor('');
    setError(null);
    setBusy(false);
    setPageId(null);
    setBlockId(null);
    setTags([]);
  }, []);

  const openSealDialog = React.useCallback(
    (nextPageId: string | null | undefined, nextBlockId: string): boolean => {
      // The caller may pass the page explicitly (the drag-handle menu knows
      // it) or leave it out (the slash-menu item does not — it reads the
      // CURRENT page from here). Falling back keeps one code path.
      const targetPageId = nextPageId ?? currentPageId;
      if (!targetPageId || !nextBlockId) return false;
      setPageId(targetPageId);
      setBlockId(nextBlockId);
      setPassphrase('');
      setAnchor('');
      setError(null);
      // The label and the mode come from the STORED state, not from a memo:
      // the block may have been sealed in another tab.
      setOpening(isBlockEncrypted(targetPageId, nextBlockId));
      // F 487 — load the block's EXISTING tags, so an already-sealed block
      // shows the tag the operator can adjust. `blockAnchors` reads the
      // stored page content, which is the authority.
      setTags(
        isBlockEncrypted(targetPageId, nextBlockId)
          ? blockAnchors(targetPageId, nextBlockId).map((a) => ({
              id: a.id,
              text: a.text,
              kind: a.kind,
              source: a.source,
            }))
          : [],
      );
      setOpened(true);
      return true;
    },
    [currentPageId],
  );

  /**
   * F 487 — save an adjusted tag.
   *
   * No passphrase is required: a tag is PLAINTEXT by design, because its
   * whole purpose is to let a reader without the key find the block.
   */
  const saveTags = React.useCallback(
    async (next: BlockSealTag[]) => {
      if (!pageId || !blockId) {
        setError('No active block or page.');
        return;
      }
      const result = await setBlockAnchors({
        pageId,
        blockId,
        anchors: next.map((t) => ({
          id: t.id,
          text: t.text,
          kind: t.kind ?? 'hint',
          source: t.source ?? 'user',
        })),
      });
      if (result.ok) {
        setTags(result.value.map((a) => ({
          id: a.id,
          text: a.text,
          kind: a.kind,
          source: a.source,
        })));
        setError(null);
        // The stored block changed, so the editor must be told to refresh.
        onSealedBlockChange?.(blockId, 'encrypt');
      } else {
        setError(result.error || 'The tag could not be saved.');
      }
    },
    [pageId, blockId, onSealedBlockChange],
  );

  /**
   * Install the opener the slash-menu item calls.
   *
   * The slash menu's `getItems` is a CALLBACK, not a component, so it
   * cannot call `useBlockSeal()`. It reads this module-level ref instead.
   * The ref is installed on mount and removed on unmount, so a stale opener
   * can never seal a block into a page that is no longer open.
   */
  React.useEffect(() => {
    setSealOpener({
      pageId: currentPageId,
      openSealDialog: (p, b) => openSealDialog(p ?? currentPageId, b),
    });
    return () => setSealOpener(null);
  }, [currentPageId, openSealDialog]);

  const submit = React.useCallback(async () => {
    if (busy) return;
    if (!pageId || !blockId) {
      setError('No active block or page.');
      return;
    }
    if (!passphrase) {
      setError('A passphrase is required.');
      return;
    }
    if (!opening && !anchor.trim()) {
      setError(
        'A public anchor is required — the AI uses it to find this block without reading the secret.',
      );
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = opening
        ? await decryptBlock({ pageId, blockId, passphrase })
        : await encryptBlock({ pageId, blockId, passphrase, anchor });
      if (result.ok) {
        onSealedBlockChange?.(blockId, opening ? 'decrypt' : 'encrypt');
        // F 487 — after a SEAL, the tag the operator just wrote is read back
        // so the dialog shows it. The operator's flow is "seal, THEN see
        // the tag, and adjust it", so the tag must be visible in the same
        // sitting.
        if (!opening) {
          setTags(
            blockAnchors(pageId, blockId).map((a) => ({
              id: a.id,
              text: a.text,
              kind: a.kind,
              source: a.source,
            })),
          );
          // The dialog stays open after a seal so the tag can be adjusted
          // straight away. The passphrase is cleared — it is not needed to
          // edit a plaintext tag.
          setPassphrase('');
          setOpening(true);
          return;
        }
        close();
      } else {
        // The two arms of the union are `{ok:true,value}` and
        // `{ok:false,error}`; `ok` discriminates them, so the `false` arm
        // narrows to the error shape without a cast.
        setError(result.error || 'Encryption failed.');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, [busy, pageId, blockId, passphrase, anchor, opening, onSealedBlockChange, close]);

  const contextValue = React.useMemo<BlockSealContextValue>(
    () => ({ openSealDialog, onSealedBlockChange }),
    [openSealDialog, onSealedBlockChange],
  );

  return (
    <BlockSealContext.Provider value={contextValue}>
      {/* The tree that can open the dialog — the editor, and through it the
          drag-handle menu and the slash menu. */}
      {children}
      <BlockSealModal
        opened={opened}
        opening={opening}
        passphrase={passphrase}
        anchor={anchor}
        error={error}
        busy={busy}
        onPassphraseChange={setPassphrase}
        onAnchorChange={setAnchor}
        onSubmit={() => { void submit(); }}
        onClose={close}
        existingAnchors={tags}
        onAnchorsChange={(next) => { void saveTags(next); }}
      />
    </BlockSealContext.Provider>
  );
};

export default BlockSealController;
